import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Wallet, TrendingUp, CheckCircle2,
  ArrowDownToLine, RefreshCw, ChevronDown, ChevronUp,
  Banknote, History, CreditCard, AlertCircle, Clipboard,
} from "lucide-react";
import { Card } from "../../../components/ui/Card";
import { Button } from "../../../components/ui/Button";
import { Modal } from "../../../components/ui/Modal";
import { LoadingSpinner } from "../../../components/ui/LoadingSpinner";
import { EmptyState } from "../../../components/ui/EmptyState";
import { useToast } from "../../../hooks/useToast";
import { ToastContainer } from "../../../components/ui/Toast";
import { useAuth } from "../../../hooks/useAuth";
import {
  useMyWallet, useMyWalletTransactions, useMyWalletEncashments, useRequestEncashment,
  useMyWalletAdvances, useRequestWalletAdvance,
  useMyServiceRequests, useRequestService,
} from "../../../hooks/useWallet";
import { useStoreServices } from "../../../hooks/useStore";
import { formatCurrency, formatDate } from "../../../utils/format";
import type { WalletEncashment, WalletTransaction, WalletAdvance, ServiceRequest } from "../../../lib/walletApi";

// ── Helpers ───────────────────────────────────────────────────────────────────

const inputClass =
  "w-full rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#2447d9]";

const STATUS_STYLES: Record<string, string> = {
  PENDING: "bg-[#fef9c3] text-[#a16207]",
  APPROVED: "bg-[#dbeafe] text-[#1d4ed8]",
  RELEASED: "bg-[#dcfce7] text-[#15803d]",
  REJECTED: "bg-[#fee2e2] text-[#b91c1c]",
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLES[status] ?? "bg-[#f1f5f9] text-[#64748b]"}`}>
      {status.charAt(0) + status.slice(1).toLowerCase()}
    </span>
  );
}

// ── Request Encashment Modal ──────────────────────────────────────────────────

function RequestEncashmentModal({
  businessId,
  availableBalance,
  employeeName,
  onClose,
}: {
  businessId: string;
  availableBalance: number;
  employeeName?: string;
  onClose: () => void;
}) {
  const { toasts, toast, dismissToast } = useToast();
  const requestEncashment = useRequestEncashment();
  const [amount, setAmount] = useState("");

  const parsed = parseFloat(amount);
  const isValid = !isNaN(parsed) && parsed > 0 && parsed <= availableBalance;

  const handleSubmit = async () => {
    if (!isValid) return;
    try {
      await requestEncashment.mutateAsync({ businessId, amount: parsed, employeeName });
      toast.success("Encashment request submitted!");
      setTimeout(onClose, 900);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      if (msg.includes("INSUFFICIENT_BALANCE")) {
        toast.error("Insufficient balance. Please check your available balance.");
      } else {
        toast.error("Failed to submit request. Please try again.");
      }
    }
  };

  return (
    <>
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      <div className="space-y-5">
        <div className="rounded-xl bg-gradient-to-br from-[#eff3ff] to-[#e0e7ff] p-4">
          <p className="text-xs font-medium text-[#4f46e5] mb-1">Available Balance</p>
          <p className="text-3xl font-bold text-[#2447d9]">{formatCurrency(availableBalance)}</p>
        </div>

        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Amount to Encash</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-[#94a3b8]">₱</span>
            <input
              type="number"
              min="1"
              max={availableBalance}
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className={`${inputClass} pl-7`}
            />
          </div>
          {amount && !isNaN(parsed) && parsed > availableBalance && (
            <p className="mt-1 text-xs text-[#dc2626]">Amount exceeds available balance of {formatCurrency(availableBalance)}.</p>
          )}
          <p className="mt-1.5 text-xs text-[#94a3b8]">
            Maximum: {formatCurrency(availableBalance)}
          </p>
        </div>

        <div className="rounded-lg bg-[#fef9c3] border border-[#fde68a] px-4 py-3 text-xs text-[#92400e]">
          Once submitted, your request will be reviewed by the business admin. You will receive a notification when it is approved and when payment is released.
        </div>

        <div className="flex gap-3 pt-1">
          <Button variant="secondary" onClick={onClose} fullWidth>Cancel</Button>
          <Button onClick={handleSubmit} disabled={requestEncashment.isPending || !isValid} fullWidth>
            {requestEncashment.isPending ? "Submitting…" : "Submit Request"}
          </Button>
        </div>
      </div>
    </>
  );
}

// ── Request Wallet Advance Modal ──────────────────────────────────────────────

function RequestWalletAdvanceModal({
  businessId,
  advanceLimit,
  currentOutstanding,
  availableBalance,
  employeeName,
  onClose,
}: {
  businessId: string;
  advanceLimit: number;
  currentOutstanding: number;
  availableBalance: number;
  employeeName?: string;
  onClose: () => void;
}) {
  const { toasts, toast, dismissToast } = useToast();
  const requestAdvance = useRequestWalletAdvance();
  const [amount, setAmount] = useState("");

  const maxAllowed = Math.max(0, Math.min(advanceLimit, advanceLimit + availableBalance));
  const parsed = parseFloat(amount);
  const isValid = !isNaN(parsed) && parsed > 0 && parsed <= maxAllowed;

  const handleSubmit = async () => {
    if (!isValid) return;
    try {
      await requestAdvance.mutateAsync({ businessId, amount: parsed, employeeName });
      toast.success("Cash advance request submitted!");
      setTimeout(onClose, 900);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      if (msg.includes("LIMIT_EXCEEDED")) {
        toast.error("Request exceeds the allowed advance limit.");
      } else if (msg.includes("WALLET_ADVANCE_NOT_CONFIGURED")) {
        toast.error("Wallet advance is not available for this business.");
      } else {
        toast.error("Failed to submit request. Please try again.");
      }
    }
  };

  return (
    <>
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      <div className="space-y-5">
        <div className="rounded-xl bg-gradient-to-br from-[#eff3ff] to-[#e0e7ff] p-4">
          <p className="text-xs font-medium text-[#4f46e5] mb-1">Available Advance Limit</p>
          <p className="text-3xl font-bold text-[#2447d9]">{formatCurrency(maxAllowed)}</p>
          <p className="text-xs text-[#818cf8] mt-0.5">Business limit: {formatCurrency(advanceLimit)} · Outstanding: {formatCurrency(currentOutstanding)}</p>
        </div>

        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Amount to Request</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-[#94a3b8]">₱</span>
            <input
              type="number"
              min="1"
              max={maxAllowed}
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className={`${inputClass} pl-7`}
            />
          </div>
          {amount && !isNaN(parsed) && parsed > maxAllowed && (
            <p className="mt-1 text-xs text-[#dc2626]">Amount exceeds your remaining advance limit of {formatCurrency(maxAllowed)}.</p>
          )}
        </div>

        <div className="rounded-lg bg-[#eff3ff] border border-[#c7d2fe] px-4 py-3 text-xs text-[#3730a3]">
          This advance will be deducted from your future commission earnings once you have sufficient balance. Admin will approve and release the cash.
        </div>

        <div className="flex gap-3 pt-1">
          <Button variant="secondary" onClick={onClose} fullWidth>Cancel</Button>
          <Button onClick={handleSubmit} disabled={requestAdvance.isPending || !isValid} fullWidth>
            {requestAdvance.isPending ? "Submitting…" : "Submit Request"}
          </Button>
        </div>
      </div>
    </>
  );
}

// ── Request Service Modal ─────────────────────────────────────────────────────

function RequestServiceModal({
  businessId,
  onClose,
}: {
  businessId: string;
  onClose: () => void;
}) {
  const { toasts, toast, dismissToast } = useToast();
  const requestService = useRequestService();
  const { data: services = [], isLoading: servicesLoading } = useStoreServices(businessId);
  const [selectedServiceId, setSelectedServiceId] = useState("");
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");

  const selectedService = services.find((s) => s.serviceId === selectedServiceId);
  const isAmountLocked = !!selectedService?.lockAmount;
  const serviceHasPrice = selectedService?.serviceAmount !== undefined && selectedService.serviceAmount > 0;
  const parsed = parseFloat(amount);
  const isValid = !!selectedService && !isNaN(parsed) && parsed >= 0;

  const handleServiceChange = (serviceId: string) => {
    setSelectedServiceId(serviceId);
    const svc = services.find((s) => s.serviceId === serviceId);
    if (svc?.serviceAmount !== undefined) {
      setAmount(String(svc.serviceAmount));
    } else {
      setAmount("");
    }
  };

  const handleSubmit = async () => {
    if (!isValid || !selectedService) return;
    try {
      await requestService.mutateAsync({
        businessId,
        serviceId: selectedService.serviceId,
        description: selectedService.name,
        notes: notes.trim() || undefined,
        amount: parsed,
      });
      toast.success("Service request submitted!");
      setTimeout(onClose, 900);
    } catch {
      toast.error("Failed to submit request. Please try again.");
    }
  };

  return (
    <>
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      <div className="space-y-4">
        <div className="rounded-xl bg-gradient-to-br from-[#eff3ff] to-[#e0e7ff] p-4">
          <p className="text-xs font-medium text-[#4f46e5] mb-1">Submit a Service Request</p>
          <p className="text-xs text-[#6366f1]">Select the service you rendered and enter the service amount. Admin will review and approve with a commission.</p>
        </div>

        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Service</label>
          {servicesLoading ? (
            <div className="flex items-center gap-2 py-2"><LoadingSpinner size="sm" /><span className="text-sm text-[#64748b]">Loading services…</span></div>
          ) : services.length === 0 ? (
            <p className="text-sm text-[#94a3b8] py-2">No services configured for this business yet.</p>
          ) : (
            <select
              value={selectedServiceId}
              onChange={(e) => handleServiceChange(e.target.value)}
              className={inputClass}
            >
              <option value="">— Select a service —</option>
              {services.map((s) => (
                <option key={s.serviceId} value={s.serviceId}>{s.name}{s.serviceAmount ? ` — ₱${s.serviceAmount.toLocaleString("en-PH")}` : ""}</option>
              ))}
            </select>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Service Amount (₱)</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-[#94a3b8]">₱</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => !isAmountLocked && setAmount(e.target.value)}
              placeholder="0.00"
              disabled={isAmountLocked}
              className={`${inputClass} pl-7 ${isAmountLocked ? "bg-[#f8fafc] text-[#64748b] cursor-not-allowed" : ""}`}
            />
            {isAmountLocked && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94a3b8]">
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
              </span>
            )}
          </div>
          {isAmountLocked ? (
            <p className="mt-1 text-xs text-[#2447d9]">Amount is fixed by the admin and cannot be changed.</p>
          ) : serviceHasPrice ? (
            <p className="mt-1 text-xs text-[#64748b]">Pre-filled from service configuration — edit if the actual amount differs.</p>
          ) : null}
        </div>

        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Additional Notes (optional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder="Any extra details about this service…"
            className={inputClass}
          />
        </div>

        <div className="flex gap-3 pt-1">
          <Button variant="secondary" onClick={onClose} fullWidth>Cancel</Button>
          <Button onClick={handleSubmit} disabled={requestService.isPending || !isValid} fullWidth>
            {requestService.isPending ? "Submitting…" : "Submit Request"}
          </Button>
        </div>
      </div>
    </>
  );
}

// ── Service Request Row ───────────────────────────────────────────────────────

function ServiceRequestRow({ req }: { req: ServiceRequest }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="hover:bg-[#f8fafc] cursor-pointer" onClick={() => setOpen((v) => !v)}>
        <td className="px-5 py-3 whitespace-nowrap text-[#64748b] text-sm">{formatDate(req.createdAt)}</td>
        <td className="px-5 py-3">
          <p className="text-sm font-medium text-[#0f172a]">{req.description}</p>
          <p className="text-xs text-[#94a3b8]">Service: {formatCurrency(req.amount)}</p>
        </td>
        <td className="px-5 py-3 text-sm font-semibold text-[#16a34a]">
          {req.commissionAmount !== undefined ? `+${formatCurrency(req.commissionAmount)}` : "—"}
        </td>
        <td className="px-5 py-3">
          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLES[req.status] ?? "bg-[#f1f5f9] text-[#64748b]"}`}>
            {req.status.charAt(0) + req.status.slice(1).toLowerCase()}
          </span>
        </td>
        <td className="px-5 py-3 text-[#94a3b8]">
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </td>
      </tr>
      {open && (
        <tr className="bg-[#f8fafc]">
          <td colSpan={5} className="px-5 pb-3 pt-0">
            <div className="text-xs space-y-1 text-[#64748b]">
              {req.notes && <p><span className="font-medium">Additional notes:</span> {req.notes}</p>}
              {req.reviewedAt && <p><span className="font-medium">Reviewed:</span> {formatDate(req.reviewedAt)} by {req.reviewedBy}</p>}
              {req.reviewNotes && <p><span className="font-medium">Admin note:</span> {req.reviewNotes}</p>}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ── Advance Row ───────────────────────────────────────────────────────────────

function AdvanceRow({ adv }: { adv: WalletAdvance }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="hover:bg-[#f8fafc] cursor-pointer" onClick={() => setOpen((v) => !v)}>
        <td className="px-5 py-3 whitespace-nowrap text-[#64748b] text-sm">{formatDate(adv.requestedAt)}</td>
        <td className="px-5 py-3 text-sm font-semibold text-[#0f172a]">{formatCurrency(adv.amount)}</td>
        <td className="px-5 py-3 text-xs text-[#64748b]">
          {adv.remainingAmount < adv.amount
            ? <span className="text-[#16a34a]">Partially repaid · {formatCurrency(adv.remainingAmount)} left</span>
            : <span>Outstanding</span>}
        </td>
        <td className="px-5 py-3">
          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLES[adv.status] ?? "bg-[#f1f5f9] text-[#64748b]"}`}>
            {adv.status.charAt(0) + adv.status.slice(1).toLowerCase()}
          </span>
        </td>
        <td className="px-5 py-3 text-[#94a3b8]">
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </td>
      </tr>
      {open && (
        <tr className="bg-[#f8fafc]">
          <td colSpan={5} className="px-5 pb-3 pt-0">
            <div className="text-xs space-y-1 text-[#64748b]">
              {adv.approvedAt && <p><span className="font-medium">Approved:</span> {formatDate(adv.approvedAt)} by {adv.approvedBy}</p>}
              {adv.releasedAt && <p><span className="font-medium">Released:</span> {formatDate(adv.releasedAt)} by {adv.releasedBy}</p>}
              {adv.rejectedAt && <p><span className="font-medium">Rejected:</span> {formatDate(adv.rejectedAt)} by {adv.rejectedBy}</p>}
              {adv.comments && <p><span className="font-medium">Note:</span> {adv.comments}</p>}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ── Transaction Row ───────────────────────────────────────────────────────────

function TxnRow({ txn }: { txn: WalletTransaction }) {
  return (
    <tr className="hover:bg-[#f8fafc]">
      <td className="px-5 py-3 whitespace-nowrap text-[#64748b] text-sm">{formatDate(txn.date)}</td>
      <td className="px-5 py-3">
        <p className="text-sm font-medium text-[#0f172a]">{txn.serviceName}</p>
        <p className="text-xs text-[#94a3b8]">{formatCurrency(txn.price)} service · {txn.commissionRate}% rate</p>
      </td>
      <td className="px-5 py-3 text-right text-sm font-semibold text-[#16a34a]">+{formatCurrency(txn.commissionAmount)}</td>
    </tr>
  );
}

// ── Encashment Row ────────────────────────────────────────────────────────────

function EncashmentRow({ enc }: { enc: WalletEncashment }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr
        className="hover:bg-[#f8fafc] cursor-pointer"
        onClick={() => setOpen((v) => !v)}
      >
        <td className="px-5 py-3 whitespace-nowrap text-[#64748b] text-sm">{formatDate(enc.requestedAt)}</td>
        <td className="px-5 py-3 text-sm font-semibold text-[#374151]">{formatCurrency(enc.amount)}</td>
        <td className="px-5 py-3"><StatusBadge status={enc.status} /></td>
        <td className="px-5 py-3 text-[#94a3b8]">
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </td>
      </tr>
      {open && (
        <tr className="bg-[#f8fafc]">
          <td colSpan={4} className="px-5 pb-3 pt-0">
            <div className="text-xs space-y-1 text-[#64748b]">
              {enc.approvedAt && <p><span className="font-medium">Approved:</span> {formatDate(enc.approvedAt)} by {enc.approvedBy}</p>}
              {enc.releasedAt && <p><span className="font-medium">Released:</span> {formatDate(enc.releasedAt)} by {enc.releasedBy}</p>}
              {enc.rejectedAt && <p><span className="font-medium">Rejected:</span> {formatDate(enc.rejectedAt)} by {enc.rejectedBy}</p>}
              {enc.comments && <p><span className="font-medium">Note:</span> {enc.comments}</p>}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

type TabKey = "transactions" | "encashments" | "advances" | "service-requests";

export default function WalletPage() {
  const { activeBusinessId, user } = useAuth();
  const employeeName = user ? `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() || undefined : undefined;
  const { data, isLoading, refetch } = useMyWallet(activeBusinessId);
  const {
    data: txnData,
    fetchNextPage: loadMoreTxns,
    hasNextPage: hasMoreTxns,
    isFetchingNextPage: txnsFetchingMore,
    isLoading: txnsLoading,
  } = useMyWalletTransactions(activeBusinessId);
  const {
    data: encData,
    fetchNextPage: loadMoreEncs,
    hasNextPage: hasMoreEncs,
    isFetchingNextPage: encsFetchingMore,
    isLoading: encsLoading,
  } = useMyWalletEncashments(activeBusinessId);
  const { data: advData, isLoading: advsLoading } = useMyWalletAdvances(activeBusinessId);
  const { data: serviceReqData, isLoading: serviceReqsLoading } = useMyServiceRequests(activeBusinessId);
  const [showEncash, setShowEncash] = useState(false);
  const [showAdvance, setShowAdvance] = useState(false);
  const [showRequestService, setShowRequestService] = useState(false);
  const [searchParams] = useSearchParams();
  const VALID_TABS: TabKey[] = ["transactions", "encashments", "advances", "service-requests"];
  const paramTab = searchParams.get("tab") as TabKey | null;
  const [tab, setTab] = useState<TabKey>(
    paramTab && VALID_TABS.includes(paramTab) ? paramTab : "transactions",
  );

  useEffect(() => {
    if (paramTab && VALID_TABS.includes(paramTab)) setTab(paramTab);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramTab]);

  if (!activeBusinessId) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#eff3ff]">
            <Wallet className="h-4 w-4 text-[#2447d9]" />
          </div>
          <h1 className="text-2xl font-bold text-[#0f172a]">My Wallet</h1>
        </div>
        <EmptyState
          icon={<Wallet className="h-8 w-8 text-[#94a3b8]" />}
          title="No active business"
          description="Select a business from the top bar to view your wallet."
        />
      </div>
    );
  }

  const balance = data?.balance;
  const transactions = txnData?.pages.flatMap((p) => p.items) ?? [];
  const encashments = encData?.pages.flatMap((p) => p.items) ?? [];
  const advances = advData?.items ?? [];
  const serviceRequests = serviceReqData ?? [];
  const outstandingAdvances = advances.filter((a) => a.status === "PENDING" || a.status === "APPROVED" || a.status === "RELEASED");
  const totalOutstanding = outstandingAdvances.reduce((s, a) => s + a.remainingAmount, 0);
  const pendingServiceReqs = serviceRequests.filter((r) => r.status === "PENDING");
  const isPageLoading = isLoading || txnsLoading || encsLoading || advsLoading || serviceReqsLoading;

  return (
    <div className="space-y-5">

      {/* ── Hero balance card ─────────────────────────────────────────────── */}
      <div className="relative rounded-2xl bg-gradient-to-br from-[#2447d9] to-[#1a33a8] p-6 shadow-lg overflow-hidden">
        {/* Decorative circles */}
        <div className="pointer-events-none absolute -top-10 -right-10 h-48 w-48 rounded-full bg-white/5" />
        <div className="pointer-events-none absolute -bottom-8 -left-8 h-36 w-36 rounded-full bg-white/5" />

        {/* Refresh button */}
        <button
          onClick={() => refetch()}
          className="absolute top-4 right-4 flex h-8 w-8 items-center justify-center rounded-lg bg-white/10 text-white/70 hover:bg-white/20 hover:text-white transition-colors"
          title="Refresh"
        >
          <RefreshCw className="h-4 w-4" />
        </button>

        {/* Title */}
        <div className="flex items-center gap-2 mb-5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/15">
            <Wallet className="h-4 w-4 text-white" />
          </div>
          <div>
            <p className="text-xs font-medium text-white/60 uppercase tracking-wide">My Wallet</p>
          </div>
        </div>

        {/* Available balance — hero number */}
        {isPageLoading ? (
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-48 rounded-lg bg-white/10 animate-pulse" />
          </div>
        ) : (
          <div className="mb-6">
            <p className="text-xs font-medium text-white/60 mb-1">Available Balance</p>
            <p className={`text-4xl font-bold tracking-tight ${(balance?.availableBalance ?? 0) < 0 ? "text-[#fca5a5]" : "text-white"}`}>
              {formatCurrency(balance?.availableBalance ?? 0)}
            </p>
            {(balance?.availableBalance ?? 0) < 0 && (
              <p className="text-xs text-[#fca5a5] mt-0.5">Advance outstanding</p>
            )}
          </div>
        )}

        {/* Secondary stats row */}
        {!isPageLoading && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 mb-6">
            <div className="rounded-xl bg-white/10 px-3 py-2.5">
              <p className="text-[10px] font-medium text-white/50 uppercase tracking-wide mb-0.5">Total Earned</p>
              <p className="text-sm font-bold text-white">{formatCurrency(balance?.totalEarned ?? 0)}</p>
              {(balance?.totalCredits ?? 0) > 0 && (
                <p className="text-[10px] text-white/40 mt-0.5">Incl. {formatCurrency(balance!.totalCredits)} credits</p>
              )}
            </div>
            <div className="rounded-xl bg-white/10 px-3 py-2.5">
              <p className="text-[10px] font-medium text-white/50 uppercase tracking-wide mb-0.5">Advances Out</p>
              <p className={`text-sm font-bold ${totalOutstanding > 0 ? "text-[#fde68a]" : "text-white"}`}>
                {formatCurrency(totalOutstanding)}
              </p>
              <p className="text-[10px] text-white/40 mt-0.5">{outstandingAdvances.length} outstanding</p>
            </div>
            <div className="col-span-2 sm:col-span-1 rounded-xl bg-white/10 px-3 py-2.5">
              <p className="text-[10px] font-medium text-white/50 uppercase tracking-wide mb-0.5">Pending Encash</p>
              <p className={`text-sm font-bold ${(balance?.pendingEncashment ?? 0) > 0 ? "text-[#fde68a]" : "text-white"}`}>
                {formatCurrency(balance?.pendingEncashment ?? 0)}
              </p>
              <p className="text-[10px] text-white/40 mt-0.5">Awaiting approval</p>
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowEncash(true)}
            disabled={isLoading || (balance?.availableBalance ?? 0) <= 0}
            className="flex items-center gap-1.5 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-[#2447d9] hover:bg-white/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
          >
            <ArrowDownToLine className="h-4 w-4" />
            Request Encashment
          </button>
          <button
            onClick={() => setShowAdvance(true)}
            disabled={isLoading || advsLoading || !data?.walletAdvanceLimit}
            title={!data?.walletAdvanceLimit ? "Cash advance not available for this business" : undefined}
            className="flex items-center gap-1.5 rounded-lg bg-white/15 px-4 py-2 text-sm font-medium text-white hover:bg-white/25 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <CreditCard className="h-4 w-4" />
            Cash Advance
          </button>
          <button
            onClick={() => setShowRequestService(true)}
            disabled={isLoading}
            className="flex items-center gap-1.5 rounded-lg bg-white/15 px-4 py-2 text-sm font-medium text-white hover:bg-white/25 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Clipboard className="h-4 w-4" />
            Request Service
          </button>
        </div>
      </div>

      {/* ── Tabs ──────────────────────────────────────────────────────────── */}
      {!isPageLoading && (
        <>
          <div>
            <div className="flex gap-1 border-b border-[#e2e8f0] overflow-x-auto overflow-y-hidden">
              {[
                { key: "transactions" as TabKey, label: "Commission History", icon: <History className="h-4 w-4" />, badge: 0 },
                { key: "encashments" as TabKey, label: "Encashments", icon: <Banknote className="h-4 w-4" />, badge: encashments.filter((e) => e.status === "PENDING").length },
                { key: "advances" as TabKey, label: "Cash Advances", icon: <CreditCard className="h-4 w-4" />, badge: outstandingAdvances.length },
                { key: "service-requests" as TabKey, label: "Service Requests", icon: <Clipboard className="h-4 w-4" />, badge: pendingServiceReqs.length },
              ].map(({ key, label, icon, badge }) => (
                <button
                  key={key}
                  onClick={() => setTab(key)}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px whitespace-nowrap ${
                    tab === key ? "border-[#2447d9] text-[#2447d9]" : "border-transparent text-[#64748b] hover:text-[#0f172a]"
                  }`}
                >
                  {icon}{label}
                  {badge > 0 && (
                    <span className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full bg-[#2447d9] text-[10px] font-bold text-white">
                      {badge}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* ── Commission History Tab ─────────────────────────────────────── */}
          {tab === "transactions" && (
            <Card padding="none">
              {transactions.length === 0 ? (
                <EmptyState
                  icon={<TrendingUp className="h-7 w-7 text-[#94a3b8]" />}
                  title="No commissions yet"
                  description="Your commission earnings will appear here once services are recorded."
                  className="py-10"
                />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
                        {["Date", "Service", "Commission"].map((h, i) => (
                          <th key={h} className={`px-5 py-3 text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap ${i === 2 ? "text-right" : "text-left"}`}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#f1f5f9]">
                      {transactions.map((t) => <TxnRow key={t.txnId} txn={t} />)}
                    </tbody>
                  </table>
                  {hasMoreTxns && (
                    <div className="flex justify-center py-3 border-t border-[#f1f5f9]">
                      <button
                        onClick={() => loadMoreTxns()}
                        disabled={txnsFetchingMore}
                        className="text-sm text-[#2447d9] hover:underline font-medium disabled:opacity-50"
                      >
                        {txnsFetchingMore ? "Loading..." : "Show more"}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </Card>
          )}

          {/* ── Encashment Requests Tab ────────────────────────────────────── */}
          {tab === "encashments" && (
            <div className="space-y-4">
              {/* Released / approved summary — contextually relevant here */}
              {((balance?.approvedEncashment ?? 0) > 0 || (balance?.releasedEncashment ?? 0) > 0) && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-xl border border-[#e0f2fe] bg-[#f0f9ff] px-4 py-3">
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle2 className="h-4 w-4 text-[#0891b2]" />
                      <p className="text-xs font-medium text-[#0891b2]">Approved (Unreleased)</p>
                    </div>
                    <p className="text-lg font-bold text-[#0f172a]">{formatCurrency(balance?.approvedEncashment ?? 0)}</p>
                    <p className="text-[10px] text-[#64748b] mt-0.5">Cash being prepared</p>
                  </div>
                  <div className="rounded-xl border border-[#dcfce7] bg-[#f0fdf4] px-4 py-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Banknote className="h-4 w-4 text-[#16a34a]" />
                      <p className="text-xs font-medium text-[#16a34a]">Total Released</p>
                    </div>
                    <p className="text-lg font-bold text-[#0f172a]">{formatCurrency(balance?.releasedEncashment ?? 0)}</p>
                    <p className="text-[10px] text-[#64748b] mt-0.5">Already paid out</p>
                  </div>
                </div>
              )}
              <Card padding="none">
                {encashments.length === 0 ? (
                  <EmptyState
                    icon={<Banknote className="h-7 w-7 text-[#94a3b8]" />}
                    title="No encashment requests"
                    description="Tap 'Request Encashment' above to cash out your commissions."
                    className="py-10"
                  />
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
                          {["Requested", "Amount", "Status", ""].map((h) => (
                            <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#f1f5f9]">
                        {encashments.map((e) => <EncashmentRow key={e.encashId} enc={e} />)}
                      </tbody>
                    </table>
                    {hasMoreEncs && (
                      <div className="flex justify-center py-3 border-t border-[#f1f5f9]">
                        <button
                          onClick={() => loadMoreEncs()}
                          disabled={encsFetchingMore}
                          className="text-sm text-[#2447d9] hover:underline font-medium disabled:opacity-50"
                        >
                          {encsFetchingMore ? "Loading..." : "Show more"}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            </div>
          )}

          {/* ── Cash Advances Tab ──────────────────────────────────────────── */}
          {tab === "advances" && (
            <div className="space-y-4">
              {totalOutstanding > 0 && (
                <div className="flex items-start gap-3 rounded-xl border border-[#ddd6fe] bg-[#ede9fe] px-4 py-3">
                  <AlertCircle className="h-4 w-4 text-[#7c3aed] mt-0.5 shrink-0" />
                  <p className="text-xs text-[#5b21b6]">
                    You have <strong>{formatCurrency(totalOutstanding)}</strong> in outstanding cash advances. This amount is deducted from your available balance until repaid.
                  </p>
                </div>
              )}
              <Card padding="none">
                {advances.length === 0 ? (
                  <EmptyState
                    icon={<CreditCard className="h-7 w-7 text-[#94a3b8]" />}
                    title="No cash advances"
                    description="Tap 'Cash Advance' above to request an advance against your future commission earnings."
                    className="py-10"
                  />
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
                          {["Requested", "Amount", "Outstanding", "Status", ""].map((h) => (
                            <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#f1f5f9]">
                        {advances.map((a) => <AdvanceRow key={a.advId} adv={a} />)}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </div>
          )}

          {/* ── Service Requests Tab ───────────────────────────────────────── */}
          {tab === "service-requests" && (
            <Card padding="none">
              {serviceRequests.length === 0 ? (
                <EmptyState
                  icon={<Clipboard className="h-7 w-7 text-[#94a3b8]" />}
                  title="No service requests"
                  description="Tap 'Request Service' above to submit a service request for admin review."
                  className="py-10"
                />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
                        {["Date", "Description", "Commission", "Status", ""].map((h) => (
                          <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#f1f5f9]">
                      {serviceRequests.map((r) => <ServiceRequestRow key={r.requestId} req={r} />)}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          )}
        </>
      )}

      {/* Loading state */}
      {isPageLoading && (
        <div className="flex justify-center py-12"><LoadingSpinner /></div>
      )}

      {showEncash && balance && (
        <Modal isOpen onClose={() => setShowEncash(false)} title="Request Encashment" size="sm">
          <RequestEncashmentModal
            businessId={activeBusinessId}
            availableBalance={balance.availableBalance}
            employeeName={employeeName ?? undefined}
            onClose={() => setShowEncash(false)}
          />
        </Modal>
      )}

      {showAdvance && (
        <Modal isOpen onClose={() => setShowAdvance(false)} title="Request Cash Advance" size="sm">
          <RequestWalletAdvanceModal
            businessId={activeBusinessId}
            advanceLimit={data?.walletAdvanceLimit ?? 0}
            currentOutstanding={totalOutstanding}
            availableBalance={balance?.availableBalance ?? 0}
            employeeName={employeeName}
            onClose={() => setShowAdvance(false)}
          />
        </Modal>
      )}

      {showRequestService && (
        <Modal isOpen onClose={() => setShowRequestService(false)} title="Request Service" size="sm">
          <RequestServiceModal
            businessId={activeBusinessId}
            onClose={() => setShowRequestService(false)}
          />
        </Modal>
      )}
    </div>
  );
}
